import { useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";

export interface StudySession {
  id: string;
  subject: string | null;
  minutes: number;
  studied_on: string;
}

export interface ExamAttempt {
  id: string;
  exam_name: string;
  correct_count: number;
  wrong_count: number;
  blank_count: number;
  net: number;
  taken_on: string;
  rank: string | null;
  total_questions: number | null;
}

export interface CourseProgress {
  course_id: string;
  completed_topics: number;
  weak_count: number;
  last_studied: string | null;
}

export interface WeakTopic {
  id: string;
  subject: string | null;
  topic: string | null;
  mastery_percent: number;
}

export interface SubjectPerformance {
  id: string;
  subject: string | null;
  net: number;
  max_net: number;
  color: string | null;
}

export interface Homework {
  id: string;
  title: string;
  subject: string | null;
  teacher_name: string | null;
  due_date: string | null;
  status: string | null;
}

export interface Favorite {
  id: string;
  type: string | null;
  title: string;
  subject: string | null;
}

export interface Note {
  id: string;
  title: string;
  subject: string | null;
  content: string | null;
}

export interface WrongQuestion {
  id: string;
  subject: string | null;
  topic: string | null;
  question: string | null;
  correct_answer: string | null;
  my_answer: string | null;
}

export interface Certificate {
  id: string;
  title: string;
  description: string | null;
  earned_date: string | null;
  icon: string | null;
}

export interface UserActivity {
  studySessions: StudySession[];
  examAttempts: ExamAttempt[];
  courseProgress: CourseProgress[];
  weakTopics: WeakTopic[];
  subjectPerformance: SubjectPerformance[];
  homework: Homework[];
  favorites: Favorite[];
  notes: Note[];
  wrongQuestions: WrongQuestion[];
  certificates: Certificate[];
}

const EMPTY: UserActivity = {
  studySessions: [],
  examAttempts: [],
  courseProgress: [],
  weakTopics: [],
  subjectPerformance: [],
  homework: [],
  favorites: [],
  notes: [],
  wrongQuestions: [],
  certificates: [],
};

function toNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Bir öğrencinin tüm aktivite verilerini auth user_id üzerinden paralel çeker.
export function useUserActivity(userId: string | null) {
  const [data, setData] = useState<UserActivity>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setData(EMPTY);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const dataClient = getDataClient();

        const [
          studyRes,
          examRes,
          courseRes,
          weakRes,
          perfRes,
          hwRes,
          favRes,
          noteRes,
          wrongRes,
          certRes,
        ] = await Promise.all([
          dataClient
            .from("study_sessions")
            .select("id, subject, minutes, studied_on")
            .eq("user_id", userId)
            .order("studied_on", { ascending: false })
            .limit(50),
          dataClient
            .from("exam_attempts")
            .select(
              "id, exam_name, correct_count, wrong_count, blank_count, net, taken_on, rank, total_questions",
            )
            .eq("user_id", userId)
            .order("taken_on", { ascending: false })
            .limit(50),
          dataClient
            .from("course_progress")
            .select("course_id, completed_topics, weak_count, last_studied")
            .eq("user_id", userId),
          dataClient
            .from("weak_topics")
            .select("id, subject, topic, mastery_percent")
            .eq("user_id", userId)
            .order("mastery_percent", { ascending: true })
            .limit(50),
          dataClient
            .from("subject_performance")
            .select("id, subject, net, max_net, color")
            .eq("user_id", userId),
          dataClient
            .from("homework")
            .select("id, title, subject, teacher_name, due_date, status")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50),
          dataClient
            .from("favorites")
            .select("id, type, title, subject")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50),
          dataClient
            .from("notes")
            .select("id, title, subject, content")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50),
          dataClient
            .from("wrong_questions")
            .select("id, subject, topic, question, correct_answer, my_answer")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50),
          dataClient
            .from("user_certificates")
            .select("id, title, description, earned_date, icon")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50),
        ]);

        const failed = [
          studyRes,
          examRes,
          courseRes,
          weakRes,
          perfRes,
          hwRes,
          favRes,
          noteRes,
          wrongRes,
          certRes,
        ].find((r) => r.error);

        if (failed?.error) throw failed.error;
        if (cancelled) return;

        const exams = ((examRes.data ?? []) as ExamAttempt[]).map((r) => ({
          ...r,
          net: toNumber(r.net),
        }));
        const perf = ((perfRes.data ?? []) as SubjectPerformance[]).map((r) => ({
          ...r,
          net: toNumber(r.net),
          max_net: toNumber(r.max_net),
        }));

        setData({
          studySessions: (studyRes.data ?? []) as StudySession[],
          examAttempts: exams,
          courseProgress: (courseRes.data ?? []) as CourseProgress[],
          weakTopics: (weakRes.data ?? []) as WeakTopic[],
          subjectPerformance: perf,
          homework: (hwRes.data ?? []) as Homework[],
          favorites: (favRes.data ?? []) as Favorite[],
          notes: (noteRes.data ?? []) as Note[],
          wrongQuestions: (wrongRes.data ?? []) as WrongQuestion[],
          certificates: (certRes.data ?? []) as Certificate[],
        });
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Aktivite verileri yüklenirken bir hata oluştu.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { data, loading, error };
}